/** Shared presentational building blocks. */

import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
}) {
  // The hint sits outside the <label> on purpose: inside, it becomes part of
  // the control's accessible name, so a screen reader would announce the whole
  // legal citation every time the field takes focus.
  return (
    <div>
      <label className="block">
        <span className="field-label">
          {label}
          {required && <span className="text-accent ml-1">*</span>}
        </span>
        {children}
      </label>
      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p className="text-muted mt-1 text-xs">{hint}</p>
      ) : null}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`field-input ${props.className ?? ''}`} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`field-input ${props.className ?? ''}`} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`field-input ${props.className ?? ''}`} />;
}

export function Card({
  title,
  action,
  children,
  className = '',
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || action) && (
        <header className="mb-4 flex items-center justify-between gap-3">
          {typeof title === 'string' ? (
            <h2 className="text-heading text-sm font-semibold">{title}</h2>
          ) : (
            title
          )}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export type ToneName = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const TONE_CLASSES: Record<ToneName, string> = {
  neutral: 'bg-subtle text-body border border-line',
  success: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  warning: 'bg-amber-50 text-amber-900 border border-amber-200',
  danger: 'bg-red-50 text-red-800 border border-red-200',
  info: 'bg-accent-soft text-accent-hover border border-blue-200',
};

export function Badge({ tone = 'neutral', children }: { tone?: ToneName; children: ReactNode }) {
  return <span className={`badge ${TONE_CLASSES[tone]}`}>{children}</span>;
}

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: ToneName;
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className={`rounded-lg px-4 py-3 text-sm ${TONE_CLASSES[tone]}`}>
      {title && <p className="mb-1 font-semibold">{title}</p>}
      <div className="leading-relaxed [&_a]:underline">{children}</div>
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="border-line-strong bg-surface/60 rounded-2xl border border-dashed px-6 py-12 text-center">
      <p className="text-heading text-sm font-medium">{title}</p>
      {children && <div className="text-muted mt-2 text-sm">{children}</div>}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="text-muted inline-flex items-center gap-2 text-sm">
      <span className="border-line-strong border-t-accent h-4 w-4 animate-spin rounded-full border-2" />
      {label}
    </span>
  );
}

export function DefinitionList({
  items,
}: {
  items: ReadonlyArray<{ term: string; value: ReactNode }>;
}) {
  return (
    <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-x-4 gap-y-2 text-sm">
      {items.map((item) => (
        <div key={item.term} className="contents">
          <dt className="text-muted">{item.term}</dt>
          <dd className="text-heading">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SectionHeading({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-heading text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="text-muted mt-1 text-sm">{description}</p>}
      </div>
      {action}
    </div>
  );
}
